/**
 * Enhanced CodeBlock Component
 * Handles code display with horizontal scrolling to prevent overflow
 */

import { Box } from '@mantine/core';
import React from 'react';
import { CopyableCodeBlock } from './CopyableCodeBlock';

interface CodeBlockProps {
  children: React.ReactNode;
  className?: string;
  language?: string;
  code?: string;
  inline?: boolean;
  isDark: boolean;
}

export const CodeBlock = React.memo<CodeBlockProps>(
  ({ children, className, code, inline = false, isDark }) => {
    // Extract code content for copying
    const codeContent = code || (typeof children === 'string' ? children : '');

    // For inline code, just return styled span
    if (inline) {
      return (
        <Box
          component="code"
          style={{
            backgroundColor: isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-1)',
            color: isDark ? 'var(--mantine-color-gray-3)' : 'var(--mantine-color-dark-7)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.875em',
            fontFamily: 'var(--mantine-font-family-monospace)',
            whiteSpace: 'pre',
          }}
        >
          {children}
        </Box>
      );
    }

    // For block code, wrap in scrollable container
    return (
      <CopyableCodeBlock code={codeContent}>
        <Box
          className="chat-code-block"
          style={{
            position: 'relative',
            maxWidth: '100%',
            width: '100%',
            overflow: 'hidden',
            borderRadius: '8px',
            backgroundColor: isDark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-0)',
            border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
            marginTop: 'var(--mantine-spacing-sm)',
            marginBottom: 'var(--mantine-spacing-sm)',
            boxSizing: 'border-box',
          }}
        >
          <Box
            className="chat-code-block-scroll"
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100%',
              // Custom scrollbar styling
              scrollbarWidth: 'thin',
              scrollbarColor: isDark
                ? 'var(--mantine-color-dark-3) var(--mantine-color-dark-6)'
                : 'var(--mantine-color-gray-4) var(--mantine-color-gray-1)',
            }}
          >
            <Box
              component="pre"
              style={{
                margin: 0,
                padding: 'var(--mantine-spacing-md)',
                fontFamily: 'var(--mantine-font-family-monospace)',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: isDark ? 'var(--mantine-color-gray-3)' : 'var(--mantine-color-dark-7)',
                backgroundColor: 'transparent',
                whiteSpace: 'pre',
                wordWrap: 'normal',
                overflow: 'visible',
                minWidth: 'max-content',
                width: '100%',
              }}
            >
              <Box
                component="code"
                className={className}
                style={{
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  color: 'inherit',
                  backgroundColor: 'transparent',
                  padding: 0,
                  border: 'none',
                  whiteSpace: 'pre',
                  wordWrap: 'normal',
                }}
              >
                {children}
              </Box>
            </Box>
          </Box>
        </Box>
      </CopyableCodeBlock>
    );
  }
);

CodeBlock.displayName = 'CodeBlock';
